import React, { useContext } from "react";
import { Row, Col } from "react-grid-system";
import useDeviceSize from "../../../../../hooks/useDeviceSize";
import ExternalLink from "../../../../external-link";
import { appContext } from "providers/appProvider";

export default function Isi({ reference, expanded }) {
  const deviceSizes = useDeviceSize();
  const { appConfigs } = useContext(appContext);
  return (
    <section>
      <div className="centered-content">
        <Row>
          <Col xs={12} lg={expanded ? 12 : 6} className="col-first">
            <p role="heading" aria-level="2" className="isi-h1">
              INDICATIONS AND USAGE
            </p>
            <p>
              Nalmefene Hydrochloride Injection is indicated for the complete or
              partial reversal of opioid drug effects, including respiratory
              depression, induced by either natural or synthetic opioids.
            </p>
            <p>
              Nalmefene Hydrochloride Injection is indicated in the management
              of known or suspected opioid overdose.
            </p>
          </Col>
          <Col xs={12} lg={expanded ? 12 : 6} className="col-second">
            <p role="heading" aria-level="2" className="isi-h1 mb-10">
              {deviceSizes.lgUp || expanded
                ? "IMPORTANT SAFETY INFORMATION"
                : "IMPORTANT SAFETY INFORMATION AND INDICATIONS AND USAGE"}
            </p>
            <p role="heading" aria-level="3" className="isi-h2">
              CONTRAINDICATION
            </p>
            <p style={{ marginBottom: 10 }}>
              Nalmefene Hydrochloride injection is contraindicated in patients
              with a known hypersensitivity to the product.
            </p>
            <p role="heading" aria-level="3" className="isi-h2">
              WARNINGS AND PRECAUTIONS
            </p>
            <p role="heading" aria-level="4" className="text-bold">
              Use of Nalmefene Hydrochloride injection in Emergencies
            </p>
            <p>
              Nalmefene Hydrochloride injection, like all drugs in this class,
              is not the primary treatment for ventilatory failure. In most
              emergency settings, treatment with Nalmefene Hydrochloride
              injection should follow, not precede, the establishment of a
              patent airway, ventilatory assistance, administration of oxygen,
              and establishment of circulatory access.
            </p>
            <p role="heading" aria-level="4" className="text-bold">
              Risk of Recurrent Respiratory Depression
            </p>
            <p>
              Accidental overdose with long acting opioids [such as methadone
              and <i>levo</i>-alpha-acetylmethadol (LAAM)] may result in
              prolonged respiratory depression. Respiratory depression in both
              the postoperative and overdose setting may be complex and involve
              the effects of anesthetic agents, neuromuscular blockers, and
              other drugs. While Nalmefene Hydrochloride injection has a longer
              duration of action than naloxone in fully reversing doses, the
              physician should be aware that a recurrence of respiratory
              depression is possible, even after an apparently adequate initial
              response to Nalmefene Hydrochloride injection treatment.
            </p>
            <p className="text-bold">
              Patients treated with Nalmefene Hydrochloride injection should be
              observed until, in the opinion of the physician, there is no
              reasonable risk of recurrent respiratory depression.
            </p>
            <p role="heading" aria-level="4" className="text-bold">
              Cardiovascular Risks with Narcotic Antagonists
            </p>
            <p>
              Pulmonary edema, cardiovascular instability, hypotension,
              hypertension, ventricular tachycardia, and ventricular
              fibrillation have been reported in connection with opioid reversal
              in both postoperative and emergency department settings. In many
              cases, these effects appear to be the result of abrupt reversal of
              opioid effects.
            </p>
            <p>
              Although Nalmefene Hydrochloride injection has been used safely in
              patients with pre-&#x2060;existing cardiac disease, all drugs of
              this class should be used with caution in patients at high
              cardiovascular risk or who have received potentially cardiotoxic
              drugs.
            </p>
            <p role="heading" aria-level="4" className="text-bold">
              Risk of Precipitated Withdrawal
            </p>
            <p>
              Nalmefene Hydrochloride injection, like other opioid antagonists,
              is known to produce acute withdrawal symptoms and, therefore,
              should be used with extreme caution in patients with known
              physical dependence on opioids or following surgery involving high
              doses of opioids. Imprudent use or excessive doses of opioid
              antagonists in the postoperative setting has been associated with
              hypertension, tachycardia, and excessive mortality in patients at
              high risk for cardiovascular complications.
            </p>
            <p role="heading" aria-level="4" className="text-bold">
              Incomplete Reversal of Buprenorphine
            </p>
            <p>
              Preclinical studies have shown that nalmefene at doses up to 10
              mg/kg (437 times the maximum recommended human dose) produced
              incomplete reversal of buprenorphine-induced analgesia in animal
              models. This appears to be a consequence of a high affinity and
              slow displacement of buprenorphine from the opioid receptors.
              Hence, Nalmefene Hydrochloride injection may not completely
              reverse buprenorphine-induced respiratory depression.
            </p>
            <p role="heading" aria-level="4" className="text-bold">
              Use in Pediatric Patients
            </p>
            <p>
              Safety and effectiveness of nalmefene hydrochloride injection in
              pediatric patients have not been established.
            </p>
            <p role="heading" aria-level="3" className="isi-h2">
              ADVERSE REACTIONS
            </p>
            <p>
              The most common adverse reactions (&#62;1%) reported in clinical
              trials with nalmefene injection were nausea (18%), vomiting (9%),
              tachycardia (5%), hypertension (5%), postoperative pain (4%),
              fever (3%), and dizziness (3%).
            </p>
            <p>
              To report SUSPECTED ADVERSE REACTIONS, contact Purdue Pharma L.P.
              at{" "}
                1-888-726-7535 or FDA at{" "} 1-800-FDA-1088 or{" "}
              <ExternalLink
                externalURL="https://www.fda.gov/safety/medwatch-fda-safety-information-and-adverse-event-reporting-program"
                messageTitle={appConfigs.externalLinkInfo.TITLE}
                messageBody={appConfigs.externalLinkInfo.BODY}
                classNameLink="link plain bg-none"
                classNameOverlay={
                  appConfigs.externalLinkInfo.OVERLAY_CLASS_NAME
                }
              >
                <span className="visually-hidden">
                  FDA MedWatch website at{" "}
                </span>
                www.fda.gov/medwatch
              </ExternalLink>
              .
            </p>
          </Col>
          <Col xs={12} lg={expanded ? 12 : 6} className="col-third"></Col>

          <Col xs={12} lg={expanded ? 12 : 6} className="col-fourth">
            <p className="btm-spacer">
              Please see{" "}
              <ExternalLink
                externalURL="https://dailymed.nlm.nih.gov/dailymed/fda/fdaDrugXsl.cfm?setid=3abfbffc-9e52-4fc6-ae92-6e0a97f4afe7&type=display"
                messageTitle={appConfigs.externalLinkInfoPI.TITLE}
                messageBody={appConfigs.externalLinkInfoPI.BODY}
                classNameLink="link text-bold bg-none"
                classNameOverlay={
                  appConfigs.externalLinkInfoPI.OVERLAY_CLASS_NAME
                }
              >
                Full Prescribing Information
              </ExternalLink>
              .
            </p>
            {reference && <div className="isi-reference">{reference}</div>}
          </Col>
        </Row>
      </div>
    </section>
  );
}
